/**
 * DOCUMENTATION:
 * Component: GCPPCAQuiz
 * Purpose: Final Certification Exam for the Google Cloud Professional Architect track.
 * * ARCHITECTURAL FEATURES:
 * 1. Integrity Proctor: Uses the Page Visibility API to monitor tab switching.
 * - 3 Violations = Automatic Session Termination.
 * 2. Neural Voice: Integrated Web Speech API to narrate complex case study scenarios.
 * 3. Question Logic: Covers Technical Pillars + Mountkirk, TerramEarth, and HRL Case Studies.
 * 4. UX/UI: Material UI (MUI) with a "Command Center" aesthetic.
 * * JUNIOR DEV NOTE:
 * The 'speechSynthesis' is a native browser API. We use 'window.speechSynthesis.cancel()'
 * before each new speak command to prevent overlapping audio queues.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Card, Typography, LinearProgress, Stack,
    TextField, Fab, Zoom, Tooltip, styled, Alert, Snackbar, Paper
} from '@mui/material';
import { Link } from 'react-router-dom';

/* Icons */
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TimerIcon from '@mui/icons-material/Timer';
import GppBadIcon from '@mui/icons-material/GppBad';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PolicyIcon from '@mui/icons-material/Policy';

/* Exam Theme Constants */
const GCP_THEME = {
    bg: '#0a192d',
    card: 'rgba(25, 40, 65, 0.95)',
    blue: '#4285F4',
    red: '#EA4335',
    yellow: '#FBBC04',
    green: '#34A853',
    border: 'rgba(66, 133, 244, 0.3)',
};

/* --- GCP PCA QUESTION BANK --- */
const pcaQuestions = [
    {
        caseStudy: "Mountkirk Games",
        question: "Mountkirk needs to store game-state data that requires global strong consistency and SQL capabilities. Which service should you choose?",
        options: ["Cloud SQL with Read Replicas", "Cloud Spanner", "Cloud Bigtable", "Firestore in Native Mode"],
        answer: "Cloud Spanner",
        explanation: "Cloud Spanner is the only GCP service providing horizontal scaling with global ACID compliance and SQL support.",
    },
    {
        caseStudy: "TerramEarth",
        question: "TerramEarth wants to reduce the cost of storing 20TB of daily vehicle data that is rarely accessed after 30 days but must be kept for 7 years. Strategy?",
        options: ["Keep all data in BigQuery standard tables", "Move data to Cloud Storage and use Lifecycle Management to transition to Archive class", "Use Cloud Bigtable with HDD nodes", "Store data in Firestore"],
        answer: "Move data to Cloud Storage and use Lifecycle Management to transition to Archive class",
        explanation: "Object Lifecycle Management is the standard PCA answer for cost-optimizing long-term storage of 'cold' data.",
    },
    {
        caseStudy: "Helicopter Racing League",
        question: "HRL needs to provide low-latency video streams to fans globally. Which networking component is mandatory?",
        options: ["Standard Tier Networking", "Premium Tier Networking + Cloud CDN", "VPC Peering", "Cloud VPN"],
        answer: "Premium Tier Networking + Cloud CDN",
        explanation: "Cloud CDN (on Premium Tier) caches content at Google's global Edge POPs, reducing latency for high-bandwidth video.",
    },
    {
        caseStudy: "Technical Pillar: Networking",
        question: "An organization needs to connect its on-premises data center to GCP with a 10 Gbps dedicated connection that does not use the public internet. Choice?",
        options: ["Carrier Peering", "Cloud VPN", "Dedicated Interconnect", "Partner Interconnect"],
        answer: "Dedicated Interconnect",
        explanation: "Dedicated Interconnect provides a direct physical link to Google's network, bypassing the public internet for high-bandwidth needs.",
    },
    {
        caseStudy: "Technical Pillar: Security",
        question: "You need to ensure that a service account in Project A can write to a bucket in Project B. What is the most secure method?",
        options: ["Generate a JSON key for the SA and store it in the code", "Grant the SA 'Storage Object Creator' role on the bucket in Project B", "Make the bucket public", "Move the SA to Project B"],
        answer: "Grant the SA 'Storage Object Creator' role on the bucket in Project B",
        explanation: "This follows the 'Principle of Least Privilege' by granting only the necessary role on the specific resource across project boundaries.",
    },
    {
        caseStudy: "Technical Pillar: Compute",
        question: "A company has a Dockerized legacy app that requires custom kernel tweaks and local SSD access. Where should it be deployed?",
        options: ["Cloud Run", "App Engine Standard", "GKE Standard with custom node pools", "Cloud Functions"],
        answer: "GKE Standard with custom node pools",
        explanation: "GKE Standard allows node-level configuration (OS, kernel) which is not possible in serverless options like Cloud Run.",
    }
];

export default function GCPPCAQuiz() {
    const [phase, setPhase] = useState('welcome');
    const [name, setName] = useState('');
    const [currentQ, setCurrentQ] = useState(0);
    const [score, setScore] = useState(0);
    const [selected, setSelected] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [finished, setFinished] = useState(false);
    const [timer, setTimer] = useState(1200); // 20 minutes for PCA
    const [violations, setViolations] = useState(0);
    const [showWarning, setShowWarning] = useState(false);

    // --- Voice Synthesis Engine ---
    const speakQuestion = (text) => {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const msg = new SpeechSynthesisUtterance(text);
        msg.rate = 0.95;
        msg.pitch = 1;
        window.speechSynthesis.speak(msg);
    };

    // --- Proctor Anti-Cheat System ---
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && phase === 'quiz' && !finished) {
                setViolations(v => {
                    const newCount = v + 1;
                    if (newCount >= 3) setFinished(true); // Fail-safe
                    else setShowWarning(true);
                    return newCount;
                });
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [phase, finished]);

    // --- Global Timer ---
    useEffect(() => {
        let interval;
        if (phase === 'quiz' && !finished && timer > 0) {
            interval = setInterval(() => setTimer(t => t - 1), 1000);
        } else if (timer === 0) {
            setFinished(true);
        }
        return () => clearInterval(interval);
    }, [phase, finished, timer]);

    const handleAnswer = (opt) => {
        if (isAnswered) return;
        setSelected(opt);
        setIsAnswered(true);
        if (opt === pcaQuestions[currentQ].answer) setScore(s => s + 1);
    };

    const nextStep = () => {
        if (currentQ + 1 < pcaQuestions.length) {
            setCurrentQ(c => c + 1);
            setIsAnswered(false);
            setSelected(null);
            window.speechSynthesis.cancel();
        } else {
            setFinished(true);
        }
    };

    // --- Result Page ---
    if (finished) {
        const pass = (score / pcaQuestions.length) >= 0.8;
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: GCP_THEME.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Card sx={{ maxWidth: 700, width: '100%', bgcolor: GCP_THEME.card, color: 'white', p: 5, textAlign: 'center', border: `1px solid ${GCP_THEME.blue}` }}>
                    <CloudDoneIcon sx={{ fontSize: 100, color: pass ? GCP_THEME.green : GCP_THEME.red, mb: 2 }} />
                    <Typography variant="h3" fontWeight={900}>Certification Result</Typography>
                    <Typography variant="h6" sx={{ color: GCP_THEME.yellow, mt: 1 }}>Candidate: {name}</Typography>

                    <Box sx={{ my: 4, p: 3, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 2 }}>
                        <Typography variant="h2" fontWeight={900} color={pass ? GCP_THEME.green : GCP_THEME.red}>
                            {Math.round((score / pcaQuestions.length) * 100)}%
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            {pass ? "PROFESSIONAL CLOUD ARCHITECT CERTIFIED" : "CREDENTIAL DENIED"}
                        </Typography>
                    </Box>

                    {violations > 0 && (
                        <Alert severity="error" sx={{ mb: 4, bgcolor: 'rgba(234, 67, 53, 0.1)', color: '#ff6b6b' }}>
                            <PolicyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Proctoring alert: {violations} integrity incidents recorded.
                        </Alert>
                    )}

                    <Button component={Link} to="/gcp-dashboard" variant="contained" sx={{ bgcolor: GCP_THEME.blue, fontWeight: 800, px: 6 }}>
                        Return to Dashboard
                    </Button>
                </Card>
            </Box>
        );
    }

    // --- Welcome Page ---
    if (phase === 'welcome') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: GCP_THEME.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Card sx={{ maxWidth: 600, p: 5, bgcolor: GCP_THEME.card, color: 'white', border: `1px solid ${GCP_THEME.blue}`, borderRadius: 4 }}>
                    <Stack alignItems="center" spacing={2} mb={4}>
                        <ArchitectureIcon sx={{ fontSize: 60, color: GCP_THEME.blue }} />
                        <Typography variant="h4" fontWeight={900}>PCA Final Exam</Typography>
                        <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7 }}>
                            This exam is proctored. You must maintain focus on this window.
                            Switching tabs or minimizing the browser will result in failure.
                        </Typography>
                    </Stack>

                    <TextField
                        fullWidth
                        label="Enter Full Legal Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ mb: 4, '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: GCP_THEME.border } }, '& .MuiInputLabel-root': { color: 'gray' } }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={!name}
                        onClick={() => { setPhase('quiz'); speakQuestion("GCP Professional Architect Exam. Authorized. You may begin."); }}
                        sx={{ bgcolor: GCP_THEME.blue, color: 'white', fontWeight: 900, py: 2 }}
                    >
                        Begin Examination
                    </Button>
                </Card>
            </Box>
        );
    }

    const q = pcaQuestions[currentQ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: GCP_THEME.bg, color: 'white', p: { xs: 2, md: 6 } }}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>

                {/* HUD Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color={GCP_THEME.blue}>
                            PCA MODULE {currentQ + 1}/{pcaQuestions.length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: GCP_THEME.yellow, textTransform: 'uppercase', letterSpacing: 2 }}>
                            {q.caseStudy}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Tooltip title="Neural Read-Aloud">
                            <Fab size="small" sx={{ bgcolor: GCP_THEME.blue }} onClick={() => speakQuestion(q.question)}>
                                <VolumeUpIcon />
                            </Fab>
                        </Tooltip>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ opacity: 0.5 }}>REMAINING</Typography>
                            <Typography variant="h5" color={GCP_THEME.red} fontWeight={900}>
                                {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>

                <LinearProgress
                    variant="determinate"
                    value={(currentQ / pcaQuestions.length) * 100}
                    sx={{ height: 10, borderRadius: 5, mb: 4, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: GCP_THEME.blue } }}
                />

                <Card sx={{ bgcolor: GCP_THEME.card, color: 'white', p: 5, borderRadius: 4, border: `1px solid ${GCP_THEME.border}` }}>
                    <Typography variant="h5" sx={{ mb: 5, lineHeight: 1.5, fontWeight: 500 }}>
                        {q.question}
                    </Typography>

                    <Stack spacing={2}>
                        {q.options.map((opt, i) => (
                            <Button
                                key={i}
                                fullWidth
                                onClick={() => handleAnswer(opt)}
                                sx={{
                                    justifyContent: 'flex-start', p: 2.5, borderRadius: 2, textTransform: 'none', textAlign: 'left',
                                    bgcolor: isAnswered && opt === q.answer ? 'rgba(52, 168, 83, 0.15)' : isAnswered && opt === selected ? 'rgba(234, 67, 53, 0.15)' : 'rgba(255,255,255,0.03)',
                                    border: `1px solid ${isAnswered && opt === q.answer ? GCP_THEME.green : isAnswered && opt === selected ? GCP_THEME.red : 'rgba(255,255,255,0.1)'}`,
                                    color: 'white',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' }
                                }}
                            >
                                <Typography variant="body1">{opt}</Typography>
                            </Button>
                        ))}
                    </Stack>

                    {isAnswered && (
                        <Zoom in={isAnswered}>
                            <Box sx={{ mt: 5, p: 3, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: 3, borderLeft: `5px solid ${GCP_THEME.yellow}` }}>
                                <Typography variant="subtitle2" color={GCP_THEME.yellow} fontWeight={900} gutterBottom>ARCHITECTURAL RATIONALE:</Typography>
                                <Typography variant="body2" sx={{ mb: 4, opacity: 0.8, lineHeight: 1.7 }}>{q.explanation}</Typography>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={nextStep}
                                    sx={{ bgcolor: GCP_THEME.blue, fontWeight: 900, px: 4 }}
                                >
                                    {currentQ + 1 === pcaQuestions.length ? "Submit Final Exam" : "Proceed to Next Module"}
                                </Button>
                            </Box>
                        </Zoom>
                    )}
                </Card>

                <Snackbar open={showWarning} autoHideDuration={5000} onClose={() => setShowWarning(false)}>
                    <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                        PROCTOR ALERT: Browser focus lost. This incident has been logged. {violations}/3.
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
}
